import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "./request";
import { useGetUserCompanies } from "./query";
import { FULL_USER_COMPANIES_PAYLOAD, useCreateCategory } from "./common/noticeManagementUtils";

const BASE_URL = "/api/v1/notice_management";

// Re-export shared utilities for backward compatibility
export { FULL_USER_COMPANIES_PAYLOAD, useCreateCategory };

// Get all notices (with auto-filtering by user's companies)
export function useGetNotices(payload: any, enabled = true) {
  const { userCompanies, isFetching: isLoadingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const companyIds = (userCompanies || []).map((uc: any) => uc.id);

  const associateCompanyIds = new Set<string>();
  const locationIds = new Set<string>();
  (userCompanies || []).forEach((company: any) => {
    (company.associate_companies || []).forEach((assocCompany: any) => {
      if (assocCompany.id) {
        associateCompanyIds.add(assocCompany.id);
      }
      (assocCompany.locations || []).forEach((location: any) => {
        if (location.id) {
          locationIds.add(location.id);
        }
      });
    });
  });

  const hasCompanies = companyIds && companyIds.length > 0;
  const payloadFilters = (payload && payload.filters) || {};

  const enhancedPayload = hasCompanies ? {
    ...(payload || {}),
    filters: {
      ...payloadFilters,
      ...(payloadFilters.company_id_eq ? {} : { company_id_in: companyIds }),
    }
  } : (payload || {});

  const { data, isFetching, refetch } = useQuery(
    ["notices", enhancedPayload],
    async () => await api.get(`${BASE_URL}/notices`, enhancedPayload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && hasCompanies,
    }
  );

  const items = !hasCompanies ? [] : ((data && data.data && data.data.items) ? data.data.items : []);
  const meta = (data && data.data && data.data.meta) ? data.data.meta : {};
  const counts = (meta && meta.counts) ? meta.counts : {};

  return {
    notices: items,
    total: (meta && meta.total_count) || 0,
    counts,
    isFetching: isFetching || isLoadingCompanies,
    refetch,
    companyIds,
  };
}

// Get single notice
export function useGetNotice(id: string, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ["notice", id],
    async () => await api.get(`${BASE_URL}/notices/${id}`),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!id && enabled,
    }
  );

  return {
    notice: (data || {}).data || {},
    isFetching,
    refetch,
  };
}

// Create notice
export function useCreateNotice(onSuccess?: any, onError?: any) {
  const queryClient = useQueryClient();
  const {
    mutate: createNotice,
    error,
    isLoading: creating,
  } = useMutation(
    ["createNotice"],
    async (payload) => await api.post(`${BASE_URL}/notices`, payload),
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
            ? response.data
            : (response && response.response && response.response.data);
        queryClient.invalidateQueries(["notices"]);
        onSuccess(data);
      },
    }
  );

  return { createNotice, error, creating };
}

// Update notice
export function useUpdateNotice(onSuccess?: any, onError?: any) {
  const {
    mutate: updateNotice,
    error,
    isLoading: updating,
  } = useMutation(
    ["updateNotice"],
    async (payload: any) => {
      const { id, ...updateData } = payload;
      return await api.put(`${BASE_URL}/notices/${id}`, updateData);
    },
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
            ? response.data
            : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );

  return { updateNotice, error, updating };
}

// Change notice status
export function useChangeNoticeStatus(onSuccess?: any, onError?: any) {
  const {
    mutate: changeStatus,
    error,
    isLoading: updating,
  } = useMutation(
    ["changeNoticeStatus"],
    async (payload: any) => {
      const { id, ...statusData } = payload;
      return await api.post(`${BASE_URL}/notices/${id}/change_status`, statusData);
    },
    {
      onError,
      onSuccess: (response: any) => {
        const data =
          (response && response.status === 200)
            ? response.data
            : (response && response.response && response.response.data);
        onSuccess(data);
      },
    }
  );

  return { changeStatus, error, updating };
}

// Get notice history
export function useGetNoticeHistory(
  noticeId: string | undefined,
  fields?: string,
  enabled = true
) {
  const payload = fields ? { fields: fields } : {};

  const { data, isFetching, refetch } = useQuery(
    ["noticeHistory", noticeId],
    async () =>
      await api.get(
        `${BASE_URL}/notices/${noticeId}/history`,
        payload
      ),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && !!noticeId,
    }
  );

  const historyItems = data && data.data ? data.data : [];

  return {
    historyItems,
    isFetching,
    refetch,
  };
}

// Get notice documents
export function useGetNoticeDocuments(
  noticeId: string,
  enabled = true
) {
  const payload = {
    include: ["asset"],
    includes: ["asset"],
    filters: {
      notice_id_eq: noticeId,
    },
  };

  const { data, isFetching, refetch } = useQuery(
    ["notice_documents", noticeId],
    async () => await api.get(`${BASE_URL}/notice_documents`, payload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && !!noticeId,
    }
  );

  const items = (data && data.data && data.data.items) ? data.data.items : [];
  const meta = (data && data.data && data.data.meta) ? data.data.meta : {};

  return {
    noticeDocuments: items,
    total: (meta && meta.total_count) || 0,
    isFetching,
    refetch,
  };
}

// Get categories for notices
export function useGetNoticeCategories(
  companyId: string,
  enabled = true
) {
  const payload = {
    scopes: ["notices"],
    sorts: ["created_at desc"],
  };

  const { data, isFetching, refetch } = useQuery(
    ["notice_categories", companyId],
    async () => await api.get(`/api/v1/companies/${companyId}/categories`, payload),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: enabled && !!companyId,
    }
  );

  const items = (data && data.data && data.data.items) ? data.data.items : [];

  return {
    categories: items,
    isFetching,
    refetch,
  };
}
